import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiAlertCircle,
  FiArrowLeft,
  FiArrowRight,
  FiCalendar,
  FiCheck,
  FiCheckCircle,
  FiLoader,
  FiLock,
  FiX,
} from "react-icons/fi";
import { db, storage } from "../firebase";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  increment,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import {
  getReferralRecipientsPathForSite,
  getReferralSubmissionsPathForSite,
  REFERRAL_DEFAULT_SITE,
} from "../services/firestorePaths";
import { jsPDF } from "jspdf";
import { surveyConditionLabel } from "../constants/diseaseOptions";
import { lookupPftSurvey, submitPftSurvey } from "../services/pftSurveyPublicService";

// ── Branding ─────────────────────────────────────────────────────────────────
const LOGO_SRC = "/lung_innovations_network_logo-removebg-preview.png";
const POWERED_BY = "Lung Innovations Network in partnership with Xiva Research";

// ── PDF palette ───────────────────────────────────────────────────────────────
const INDIGO = [79, 70, 229];
const INK = [30, 41, 59];
const SLATE = [71, 85, 105];
const MUTED = [148, 163, 184];
const HAIRLINE = [226, 232, 240];

// ── Conditions ────────────────────────────────────────────────────────────────
// These are the STORED names (referral_submissions `conditions[].name`). Never
// rename one — relabel it on screen via SURVEY_CONDITION_DISPLAY in
// src/constants/diseaseOptions.js ("Pulmonary Fibrosis" shows as
// "ILD / Pulmonary Fibrosis"). Keep RawDatabaseQueryPage's CONDITION_OPTIONS
// and RawDatabasePage's DIAGNOSIS_SYNONYMS in sync when adding one.
const ALL_CONDITIONS = [
  "Asthma",
  "COPD",
  "Lung Nodules",
  "Lung Cancer",
  "Bronchiectasis",
  "Pulmonary Fibrosis",
  "Sleep Apnea",
  "Diabetes",
  "Sarcoidosis",
  "Heart Disease",
  "Cancer (other)",
];
const EXACERBATION_CONDITIONS = new Set([
  "Asthma",
  "COPD",
  "Bronchiectasis",
  "Pulmonary Fibrosis",
]);

const conditionFollowupType = (name) => {
  if (EXACERBATION_CONDITIONS.has(name)) return "exacerbation";
  if (name === "Diabetes") return "glp1";
  if (name === "Sleep Apnea") return "cpap";
  return null;
};

// ── Shared UI ────────────────────────────────────────────────────────────────
const Shell = ({ children }) => (
  <div className="relative min-h-screen w-full text-slate-100 font-[Manrope] flex flex-col">
    <div className="fixed inset-0 -z-10 overflow-hidden">
      <img
        src="/bg_ai.jpg"
        alt=""
        aria-hidden="true"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-br from-[#070418]/90 via-[#0c0726]/85 to-[#04030f]/95" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_rgba(124,58,237,0.28),_transparent_55%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom,_rgba(37,99,235,0.22),_transparent_55%)]" />
      <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-purple-600/25 blur-[140px]" />
      <div className="absolute top-1/3 -right-40 w-[520px] h-[520px] rounded-full bg-blue-600/25 blur-[140px]" />
    </div>
    <div className="flex-1 flex flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-md">
        <div className="flex flex-col items-center mb-6 text-center">
          <div className="w-16 h-16 flex items-center justify-center">
            <img src={LOGO_SRC} alt="Lung Innovations Network" className="w-16 h-16 object-contain scale-125" />
          </div>
          <span className="mt-2.5 text-[10px] uppercase tracking-[0.25em] text-slate-400 max-w-[260px]">
            {POWERED_BY}
          </span>
        </div>
        {children}
      </div>
    </div>
    <footer className="relative pb-5 px-4 text-center">
      <p className="text-[10px] uppercase tracking-[0.2em] text-slate-500 flex items-center justify-center gap-1.5">
        <FiLock size={11} /> Your information is private &amp; secure
      </p>
    </footer>
  </div>
);

const Card = ({ children }) => (
  <div className="relative rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-[0_20px_60px_-20px_rgba(124,58,237,0.5)] overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-blue-500/10 pointer-events-none" />
    <div className="absolute top-0 inset-x-0 h-[2px] bg-gradient-to-r from-purple-400 via-fuchsia-400 to-blue-400 opacity-70" />
    <div className="relative p-5 sm:p-6">{children}</div>
  </div>
);

const SectionHeader = ({ children }) => (
  <div className="flex items-center gap-2 mb-3">
    <span className="text-[10px] uppercase tracking-[0.22em] text-purple-300/80 font-semibold">
      {children}
    </span>
    <span className="h-px flex-1 bg-white/10" />
  </div>
);

// Light-mode aware: bg-white/[0.04] → rgba(255,255,255,0.72) and
// text-slate-100 → #0f172a via the existing index.css rules.
const INPUT_CLS =
  "w-full px-3.5 py-3 rounded-xl bg-white/[0.04] border border-white/10 text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-purple-400/50 focus:bg-white/[0.06] focus:ring-2 focus:ring-purple-500/20 transition";

// color-scheme handled globally in index.css (dark default, light in light-mode).
const SELECT_CLS =
  "w-full px-3 py-3 rounded-xl bg-white/[0.06] border border-white/10 text-slate-100 focus:outline-none focus:border-purple-400/50 focus:ring-2 focus:ring-purple-500/20 transition";

const PrimaryButton = ({ children, onClick, disabled, type = "button" }) => (
  <button
    type={type}
    onClick={onClick}
    disabled={disabled}
    className="group relative w-full py-3.5 rounded-xl bg-gradient-to-r from-purple-500 via-indigo-500 to-blue-500 text-white font-semibold ring-1 ring-white/20 hover:shadow-[0_10px_30px_-5px_rgba(139,92,246,0.6)] active:scale-[0.98] transition-all disabled:opacity-60 disabled:cursor-not-allowed overflow-hidden"
  >
    <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/25 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
    <span className="relative flex items-center justify-center gap-2">{children}</span>
  </button>
);

const BackButton = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="flex items-center gap-1.5 px-4 py-3 rounded-xl border border-white/10 bg-white/[0.04] text-slate-300 font-medium hover:bg-white/[0.08] transition text-sm"
  >
    <FiArrowLeft size={15} /> Back
  </button>
);

const SkipLink = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="w-full mt-3 text-sm text-slate-400 hover:text-slate-200 underline underline-offset-2 transition"
  >
    Skip to last step →
  </button>
);

const YesNo = ({ value, onChange }) => (
  <div className="flex gap-3 mt-2">
    {["yes", "no"].map((v) => (
      <button
        key={v}
        type="button"
        onClick={() => onChange(v)}
        className={`flex-1 py-3 rounded-xl font-semibold text-sm transition ${
          value === v
            ? "bg-gradient-to-r from-purple-500 to-indigo-500 text-white ring-1 ring-white/20 shadow-lg"
            : "bg-white/[0.04] border border-white/10 text-slate-300 hover:bg-white/[0.08]"
        }`}
      >
        {v === "yes" ? "Yes" : "No"}
      </button>
    ))}
  </div>
);

const FieldLabel = ({ children }) => (
  <p className="text-[11px] uppercase tracking-wider font-medium text-slate-400 mb-1.5">
    {children}
  </p>
);

const ConditionChip = ({ name, onRemove }) => (
  <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-500/20 border border-indigo-400/30 text-xs text-indigo-200 font-medium">
    {name}
    {onRemove && (
      <button type="button" onClick={onRemove} className="text-indigo-300/60 hover:text-rose-300 transition">
        <FiX size={11} />
      </button>
    )}
  </span>
);

// ── Consent text (identical to ESignPage) ────────────────────────────────────
const CONSENT_TEXT = `Research Site: Clinical Research Associates of Central PA, 190 West Park Avenue, Suite 6, Dubois, PA 15801

You have been referred by your provider to the clinical research team. By signing this form, you agree to authorize the use and disclosure (release) of your health information to the research team, as described in this Authorization Form. The following health information about you may be collected: Past medical history, medication history/lists, diagnoses, laboratory results including pathology, imaging results, cardiopulmonary results, operative reports, consultative reports, physician progress notes, and hospitalization reports.

Your health information may be used for screening purposes by the research site, the principal investigator, and their representatives. If you qualify for a research study this authorization gives Clinical Research Associates of Central PA permission to contact you regarding research studies available at our site.

You are required to sign this form to be prescreened for a research study. If you do not sign this form, it will not affect your treatment, or enrollment or eligibility for benefits. You may change your mind and revoke (take back) this authorization form at any time unless your health information is already disclosed based on this authorization form.

To revoke this Authorization, you must write to: 
Lung Innovations Network, P.C.
190 West Park Ave
Suite 6
Dubois, PA 15801. 

This Authorization will not expire unless you revoke it.

* If this is signed by an authorized personal representative of the participant, indicate the representative's relationship to the participant and/or authority to act on behalf of the participant.`;

// ── Steps labels ──────────────────────────────────────────────────────────────
const STEPS = ["About You", "Health", "Conditions", "Sign"];

// Who is signing the authorization. "self" is the participant; the rest are
// authorized personal representatives, whose relationship the consent text
// requires us to record. Labels are worded for a legal signature record.
const SIGNER_RELATIONSHIPS = [
  { value: "self", label: "The patient (I am signing for myself)" },
  { value: "spouse", label: "Spouse of the patient" },
  { value: "child", label: "Child of the patient (son or daughter)" },
  { value: "parent", label: "Parent of the patient" },
  { value: "poa", label: "Legal representative / Power of Attorney" },
];
const relationshipLabelFor = (value) =>
  (SIGNER_RELATIONSHIPS.find((o) => o.value === value) || SIGNER_RELATIONSHIPS[0]).label;

export default function ReferralQuestionnairePage({ mode } = {}) {
  // The referral link carries the site: /referral-form/:siteId/:token. Legacy
  // links (/referral-form/:token) have no siteId — those were all minted for the
  // Dubois site, which is exactly REFERRAL_DEFAULT_SITE. All referral reads/writes
  // on this unauthenticated page resolve against `site`.
  const { token, siteId } = useParams();
  const site = siteId || REFERRAL_DEFAULT_SITE;
  // mode "pft": the PFT Uploads health survey (/health-survey/:siteId/:token). Same
  // form and the same consent PDF; only the lookup and the submit differ (functions
  // pftSurveyLookup / pftSurveySubmit, never the referral collections). Without it the
  // page is exactly the referral questionnaire.
  const pft = mode === "pft";

  // This page is public-facing (patients, not staff) and should always render
  // in light mode, regardless of the app's global dark/light toggle. Force
  // the same `light-bg-mode` class the toggle uses, and restore whatever was
  // there before on unmount so the rest of the app is unaffected.
  useEffect(() => {
    const root = document.documentElement;
    const hadLightMode = root.classList.contains("light-bg-mode");
    root.classList.add("light-bg-mode");
    return () => {
      if (!hadLightMode) root.classList.remove("light-bg-mode");
    };
  }, []);

  // ── Blocking token lookup — a used link must never render the form ──────────
  // This BLOCKS the form on purpose. A single-use link that flashed the form
  // before hiding it would invite a half-filled resubmit, so we resolve the
  // token first. The lookup:
  //   1. Pre-fills the phone number from the recipient doc.
  //   2. Detects an already-submitted link (hard block — see `alreadySubmitted`).
  //   3. Captures the recipient doc ID for linking the submission.
  //
  // Fails OPEN: on a network/rules error we cannot prove the link is spent, and
  // locking out a legitimate patient is worse than showing them the form. The
  // Firestore rule on referral_submissions is the authoritative gate — this
  // block is the humane front door, not the lock.
  const [recipient, setRecipient] = useState(null);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [lookupSettled, setLookupSettled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (pft) {
      // PFT health survey: the function says whether the link was used and gives the
      // PFT's phone and address to pre-fill. Fails open like the referral lookup.
      (async () => {
        try {
          const r = await lookupPftSurvey({ siteId: site, token });
          if (cancelled) return;
          if (r && r.found) {
            setRecipient({ phone: r.phone || "", address: r.address || "" });
            if (r.submitted) setAlreadySubmitted(true);
          }
        } catch (err) {
          console.warn("Health survey lookup failed (fields won't pre-fill):", err);
        } finally {
          if (!cancelled) setLookupSettled(true);
        }
      })();
      return () => { cancelled = true; };
    }
    (async () => {
      try {
        const q = query(
          collection(db, getReferralRecipientsPathForSite(site)),
          where("token", "==", token)
        );
        const snap = await getDocs(q);
        if (cancelled) return;
        if (!snap.empty) {
          const data = { id: snap.docs[0].id, ...snap.docs[0].data() };
          setRecipient(data);
          if (data.formSubmitted) setAlreadySubmitted(true);
          // Pre-fill phone only if the user hasn't typed anything yet.
          if (data.phone) setPhone((prev) => prev || data.phone);

          // ── Additive analytics: record that this link was opened ────────────
          // Fail-soft, and never touches the existing lookup/submit workflow.
          // Lets staff measure "opened but never submitted" (open time vs. the
          // recipient's sends.*.sentAt). Only logged for not-yet-submitted links
          // so completed ones don't keep accruing opens. `firstOpenedAt` is set
          // once; `token` is preserved so the update passes Firestore rules.
          if (!data.formSubmitted) {
            try {
              await updateDoc(
                doc(db, getReferralRecipientsPathForSite(site), data.id),
                {
                  token: data.token,
                  firstOpenedAt: data.firstOpenedAt || serverTimestamp(),
                  lastOpenedAt: serverTimestamp(),
                  openCount: increment(1),
                }
              );
            } catch (e) {
              // Analytics must never block or surface on the patient's form.
              console.warn("Could not record link open (non-fatal):", e);
            }
          }
        }
      } catch (err) {
        // Rules not yet deployed or network issue — fail open (see above).
        console.warn("Referral token lookup failed (phone won't pre-fill):", err);
      } finally {
        if (!cancelled) setLookupSettled(true);
      }
    })();
    return () => { cancelled = true; };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Wizard step (1–4) ────────────────────────────────────────────────────────
  const [step, setStep] = useState(1);

  // ── Step 1: Personal info ───────────────────────────────────────────────────
  const [name, setName] = useState("");
  // dobText (MM/DD/YYYY, what's typed/shown) is the only state; the
  // canonical yyyy-mm-dd `dob` used everywhere else is derived from it. One
  // state update per keystroke instead of two — was the source of the typing
  // lag.
  const [dobText, setDobText] = useState("");
  const dobPickerRef = useRef(null);

  const dob = useMemo(() => {
    const m = dobText.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    return m ? `${m[3]}-${m[1]}-${m[2]}` : "";
  }, [dobText]);

  const handleDobTextChange = (raw) => {
    const digits = raw.replace(/[^\d]/g, "").slice(0, 8);
    let formatted = digits;
    if (digits.length > 4) formatted = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    else if (digits.length > 2) formatted = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    setDobText(formatted);
  };

  // Calendar icon → native date picker; syncs the visible MM/DD/YYYY text
  // (dob re-derives from it automatically).
  const handleDobPicked = (isoValue) => {
    if (!isoValue) { setDobText(""); return; }
    const [yyyy, mm, dd] = isoValue.split("-");
    setDobText(`${mm}/${dd}/${yyyy}`);
  };

  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [s1Error, setS1Error] = useState("");

  // Pre-fill phone from recipient
  useEffect(() => {
    if (recipient?.phone) setPhone(recipient.phone);
  }, [recipient]);
  // PFT health survey only: the address from the PFT too (the patient can change it).
  useEffect(() => {
    if (pft && recipient?.address) setAddress((prev) => prev || recipient.address);
  }, [pft, recipient]);

  const goStep2 = () => {
    if (!name.trim()) { setS1Error("Please enter your name."); return; }
    if (!dob) { setS1Error("Please enter your date of birth."); return; }
    const today = new Date();
    const dobDate = new Date(`${dob}T00:00:00`);
    const age = today.getFullYear() - dobDate.getFullYear() -
      (today < new Date(today.getFullYear(), dobDate.getMonth(), dobDate.getDate()) ? 1 : 0);
    if (age < 18) { setS1Error("You must be 18 or older to participate in this study."); return; }
    setS1Error("");
    setStep(2);
  };

  // ── Step 2: Weight management ───────────────────────────────────────────────
  const [weightInterest, setWeightInterest] = useState("");
  const [heightFt, setHeightFt] = useState("");
  const [heightIn, setHeightIn] = useState("0");
  const [weightLbs, setWeightLbs] = useState("");
  const [weightGlp1, setWeightGlp1] = useState("");

  const bmi = useMemo(() => {
    const ft = parseInt(heightFt, 10);
    const ins = parseInt(heightIn, 10) || 0;
    const lbs = parseInt(weightLbs, 10);
    if (!ft || !lbs) return null;
    const totalIn = ft * 12 + ins;
    return ((lbs / (totalIn * totalIn)) * 703).toFixed(1);
  }, [heightFt, heightIn, weightLbs]);

  const bmiCategory = useMemo(() => {
    if (!bmi) return null;
    const b = parseFloat(bmi);
    if (b < 18.5) return { label: "Underweight", color: "text-blue-300" };
    if (b < 25) return { label: "Healthy weight", color: "text-emerald-300" };
    if (b < 30) return { label: "Overweight", color: "text-amber-300" };
    return { label: "Obese", color: "text-rose-300" };
  }, [bmi]);

  // ── Step 3: Conditions ───────────────────────────────────────────────────────
  const [confirmedConditions, setConfirmedConditions] = useState([]);
  const [conditionPick, setConditionPick] = useState("");
  const [conditionFollowup, setConditionFollowup] = useState("");
  const [conditionError, setConditionError] = useState("");

  const remainingConditions = useMemo(
    () => ALL_CONDITIONS.filter((c) => !confirmedConditions.some((x) => x.name === c)),
    [confirmedConditions]
  );

  const followupType = conditionFollowupType(conditionPick);
  const isFirstRound = confirmedConditions.length === 0;
  const noMoreOption = isFirstRound ? "None of these" : "No, I'm done";

  const handleConditionPickChange = (val) => {
    setConditionPick(val);
    setConditionFollowup("");
    setConditionError("");
  };

  const confirmCondition = () => {
    if (!conditionPick || conditionPick === "__none__") return false;
    if (followupType && !conditionFollowup) {
      setConditionError("Please answer the question above.");
      return false;
    }
    const entry = { name: conditionPick };
    if (followupType === "exacerbation") entry.exacerbations = conditionFollowup;
    if (followupType === "glp1") entry.glp1ForDiabetes = conditionFollowup;
    if (followupType === "cpap") entry.usingCpap = conditionFollowup;
    setConfirmedConditions((prev) => [...prev, entry]);
    setConditionPick("");
    setConditionFollowup("");
    setConditionError("");
    return true;
  };

  const removeCondition = (name) => {
    setConfirmedConditions((prev) => prev.filter((c) => c.name !== name));
  };

  const goStep4 = () => {
    // Auto-confirm pending condition if fully answered
    if (conditionPick && conditionPick !== "__none__") {
      if (followupType && !conditionFollowup) {
        setConditionError("Please answer the question above, or use 'Skip to last step' below.");
        return;
      }
      confirmCondition();
    }
    setStep(4);
  };

  // ── Step 4: Consent & Sign ──────────────────────────────────────────────────
  const [printName, setPrintName] = useState("");
  const [signerRelationship, setSignerRelationship] = useState("self");
  const [approved, setApproved] = useState(false);
  const [showFullText, setShowFullText] = useState(false);
  const [reviewError, setReviewError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveStage, setSaveStage] = useState("");
  const [submitError, setSubmitError] = useState("");
  const [done, setDone] = useState(false);

  // Signature canvas
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const ctxRef = useRef(null);
  const drawingRef = useRef(false);
  const [hasInk, setHasInk] = useState(false);

  // Audit
  const logoRef = useRef(null);
  const openedAtRef = useRef(new Date());
  const approvedAtRef = useRef(null);
  const [clientIP, setClientIP] = useState("Unavailable");
  const [userAgent, setUserAgent] = useState("");

  useEffect(() => {
    setUserAgent(navigator.userAgent || "Unknown device");
    fetch("https://api.ipify.org?format=json")
      .then((r) => r.json())
      .then((d) => d?.ip && setClientIP(d.ip))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const img = new Image();
    img.src = LOGO_SRC;
    img.onload = () => { logoRef.current = img; };
  }, []);

  // Canvas init
  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas || !container) return;
    const ratio = window.devicePixelRatio || 1;
    const cssW = container.clientWidth;
    const cssH = 190;
    canvas.width = Math.round(cssW * ratio);
    canvas.height = Math.round(cssH * ratio);
    canvas.style.width = `${cssW}px`;
    canvas.style.height = `${cssH}px`;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, cssW, cssH);
    ctx.strokeStyle = "#c7d2fe";
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(18, cssH - 32);
    ctx.lineTo(cssW - 18, cssH - 32);
    ctx.stroke();
    ctxRef.current = ctx;
    setHasInk(false);
  }, []);

  useEffect(() => {
    if (step !== 4) return;
    let raf = 0;
    const fire = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(initCanvas); };
    // ResizeObserver fires as soon as the container has a real size (including
    // the initial observe call), so it works even if the RAF fired too early
    // while the entry animation was still computing layout.
    const ro = new ResizeObserver(fire);
    if (containerRef.current) ro.observe(containerRef.current);
    window.addEventListener("resize", fire);
    window.addEventListener("orientationchange", fire);
    return () => { ro.disconnect(); cancelAnimationFrame(raf); window.removeEventListener("resize", fire); window.removeEventListener("orientationchange", fire); };
  }, [step, initCanvas]);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const touch = e.touches && e.touches[0];
    return { x: (touch ? touch.clientX : e.clientX) - rect.left, y: (touch ? touch.clientY : e.clientY) - rect.top };
  };
  const handleStart = (e) => { e.preventDefault(); const ctx = ctxRef.current; if (!ctx) return; drawingRef.current = true; const { x, y } = getPos(e); ctx.strokeStyle = "#312e81"; ctx.lineWidth = 2.5; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.beginPath(); ctx.moveTo(x, y); };
  const handleMove = (e) => { if (!drawingRef.current) return; e.preventDefault(); const ctx = ctxRef.current; const { x, y } = getPos(e); ctx.lineTo(x, y); ctx.stroke(); if (!hasInk) setHasInk(true); };
  const handleEnd = () => { drawingRef.current = false; };
  const handleClear = () => initCanvas();

  const getSignatureDataUrl = () => {
    const src = canvasRef.current;
    const out = document.createElement("canvas");
    out.width = 500; out.height = 125;
    const octx = out.getContext("2d");
    octx.fillStyle = "#ffffff";
    octx.fillRect(0, 0, 500, 125);
    octx.drawImage(src, 0, 0, 500, 125);
    return out.toDataURL("image/png");
  };

  // ── PDF build ────────────────────────────────────────────────────────────────
  const fmtDate = (val) => {
    if (!val) return "—";
    if (val instanceof Date) return val.toLocaleString();
    return String(val);
  };

  const buildPdf = (submittedAt) => {
    const docPdf = new jsPDF({ unit: "pt", format: "letter", compress: true });
    const margin = 52;
    const pageWidth = docPdf.internal.pageSize.getWidth();
    const pageHeight = docPdf.internal.pageSize.getHeight();
    const contentWidth = pageWidth - margin * 2;
    const centerX = pageWidth / 2;

    const eyebrow = (text, x, yy) => {
      docPdf.setFont("helvetica", "bold").setFontSize(7.5).setTextColor(...INDIGO);
      docPdf.text(text.toUpperCase(), x, yy, { charSpace: 0.6 });
    };
    const hairline = (yy) => {
      docPdf.setDrawColor(...HAIRLINE).setLineWidth(0.8);
      docPdf.line(margin, yy, pageWidth - margin, yy);
    };

    let y = margin;

    // Logo
    if (logoRef.current) {
      const img = logoRef.current;
      const w = 104;
      const h = img.naturalWidth ? (img.naturalHeight / img.naturalWidth) * w : 32;
      docPdf.addImage(img, "PNG", centerX - w / 2, y, w, h);
      y += h + 6;
    }
    docPdf.setFont("helvetica", "italic").setFontSize(7.5).setTextColor(...MUTED);
    docPdf.text(POWERED_BY, centerX, y, { align: "center" });
    y += 18; hairline(y); y += 22;

    eyebrow("Release of Information", margin, y);
    y += 16;
    docPdf.setFont("helvetica", "bold").setFontSize(15).setTextColor(...INK);
    docPdf.text("Authorization to Share Information with Researcher", margin, y);
    y += 24;

    // Patient info
    const dobStr = dob ? new Date(`${dob}T00:00:00`).toLocaleDateString() : "—";
    const detailRows = [["Name", name], ["Date of Birth", dobStr], ["Phone", phone], ["Email", email || "—"]];
    const colW = contentWidth / 2;
    detailRows.forEach(([label, value], i) => {
      const col = i % 2;
      const rowTop = y + Math.floor(i / 2) * 30;
      const x = margin + col * colW;
      eyebrow(label, x, rowTop);
      docPdf.setFont("helvetica", "normal").setFontSize(10.5).setTextColor(...INK);
      docPdf.text(`${value || "—"}`, x, rowTop + 14, { maxWidth: colW - 16 });
    });
    y += Math.ceil(detailRows.length / 2) * 30 + 8;
    hairline(y); y += 18;

    // Consent body
    docPdf.setFont("helvetica", "normal").setFontSize(9).setTextColor(...SLATE);
    const wrapped = docPdf.splitTextToSize(CONSENT_TEXT, contentWidth);
    wrapped.forEach((line) => { docPdf.text(line, margin, y); y += 12.6; });
    y += 16;

    // Signature
    eyebrow("Signature", margin, y); y += 6;
    docPdf.addImage(getSignatureDataUrl(), "PNG", margin, y, 200, 50);
    y += 52;
    docPdf.setDrawColor(...HAIRLINE).setLineWidth(0.8);
    docPdf.line(margin, y, margin + 230, y);
    y += 16;
    eyebrow("Printed Name", margin, y);
    eyebrow("Signed", margin + 260, y);
    docPdf.setFont("helvetica", "normal").setFontSize(10).setTextColor(...INK);
    docPdf.text(printName, margin, y + 14);
    docPdf.text(submittedAt.toLocaleString(), margin + 260, y + 14);
    // Signer relationship — draw only if there's vertical room left on page 1;
    // it is always captured in Firestore and the page-2 audit trail regardless.
    y += 30;
    if (y + 20 < pageHeight - margin) {
      eyebrow("Relationship to Patient", margin, y);
      docPdf.setFont("helvetica", "normal").setFontSize(10).setTextColor(...INK);
      docPdf.text(relationshipLabelFor(signerRelationship), margin, y + 14);
    }

    // Page 2: audit trail
    docPdf.addPage();
    y = margin;
    if (logoRef.current) {
      const img = logoRef.current;
      const w = 84;
      const h = img.naturalWidth ? (img.naturalHeight / img.naturalWidth) * w : 26;
      docPdf.addImage(img, "PNG", centerX - w / 2, y, w, h);
      y += h + 14;
    }
    docPdf.setFont("helvetica", "bold").setFontSize(14).setTextColor(...INK);
    docPdf.text("eSignature Audit Trail", centerX, y, { align: "center" });
    y += 14;
    docPdf.setFont("helvetica", "normal").setFontSize(9).setTextColor(...MUTED);
    docPdf.text("Authorization to Share Information  •  Referral Questionnaire", centerX, y, { align: "center" });
    y += 16; hairline(y); y += 26;

    const events = [
      { label: "Referral Link Sent", desc: `Sent to ${phone} by Lung Innovations Network.` },
      { label: "Patient Opened Questionnaire", desc: `Opened on ${fmtDate(openedAtRef.current)}.` },
      { label: "Consent Reviewed & Approved", desc: approvedAtRef.current ? `Marked "I agree" on ${fmtDate(approvedAtRef.current)}.` : "Consent checkbox marked." },
      { label: "Signed & Submitted", desc: `Signed by ${printName}${signerRelationship !== "self" ? ` as ${relationshipLabelFor(signerRelationship)}` : ""} on ${fmtDate(submittedAt)}.` },
      { label: "Signer Environment", desc: `IP Address: ${clientIP}\nDevice: ${userAgent}` },
    ];

    const dotX = margin + 3;
    const textX = margin + 22;
    const textW = pageWidth - textX - margin;
    events.forEach((event, i) => {
      docPdf.setFont("helvetica", "normal").setFontSize(9);
      const descLines = docPdf.splitTextToSize(event.desc, textW);
      const blockHeight = 15 + descLines.length * 11 + 16;
      if (i < events.length - 1) {
        docPdf.setDrawColor(199, 210, 254).setLineWidth(1.2);
        docPdf.line(dotX, y + 3, dotX, y + blockHeight);
      }
      docPdf.setFillColor(...INDIGO).circle(dotX, y, 3.2, "F");
      docPdf.setFont("helvetica", "bold").setFontSize(10).setTextColor(...INK).text(event.label, textX, y + 3);
      docPdf.setFont("helvetica", "normal").setFontSize(9).setTextColor(...SLATE).text(descLines, textX, y + 15);
      y += blockHeight;
    });

    docPdf.setDrawColor(...HAIRLINE).line(margin, pageHeight - 56, pageWidth - margin, pageHeight - 56);
    docPdf.setFont("helvetica", "normal").setFontSize(8).setTextColor(...MUTED);
    docPdf.text("Generated by the RegX eSignature System", centerX, pageHeight - 40, { align: "center" });
    docPdf.text(POWERED_BY, centerX, pageHeight - 28, { align: "center" });

    return docPdf;
  };

  // ── Submit ───────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!printName.trim()) { setReviewError("Please type your full name."); return; }
    if (!approved) { setReviewError("Please check the box to agree."); return; }
    if (!hasInk) { setSubmitError("Please sign in the box above."); return; }
    setReviewError(""); setSubmitError("");
    setSaving(true);
    if (pft) {
      // PFT health survey: the same answers and the same PDF, saved by the server (one
      // use per link, enforced there). Nothing is written from this browser.
      try {
        setSaveStage("Preparing your document…");
        const docPdf = buildPdf(new Date());
        const pdfBase64 = String(docPdf.output("datauristring")).split(",")[1] || "";
        setSaveStage("Saving…");
        const answers = {
          name: name.trim(),
          dob,
          phone,
          email: email.trim(),
          address: address.trim(),
          weightManagementInterest: weightInterest || "not answered",
          conditions: confirmedConditions,
          printedName: printName.trim(),
          signerRelationship,
          signerRelationshipLabel: relationshipLabelFor(signerRelationship),
          signedByRepresentative: signerRelationship !== "self",
          ipAddress: clientIP,
          deviceInfo: userAgent,
        };
        if (weightInterest === "yes") {
          answers.height = `${heightFt}'${heightIn}"`;
          answers.weightLbs = parseInt(weightLbs, 10) || 0;
          answers.bmi = parseFloat(bmi) || 0;
          answers.onGlp1WeightMgmt = weightGlp1 || "not answered";
        }
        await submitPftSurvey({ siteId: site, token, answers, pdfBase64 });
        setDone(true);
      } catch (err) {
        console.error("Health survey submit failed:", err);
        if (err && err.alreadySubmitted) setAlreadySubmitted(true);
        else setSubmitError("Something went wrong. Please try again.");
      } finally {
        setSaving(false);
        setSaveStage("");
      }
      return;
    }
    try {
      const submittedAt = new Date();
      setSaveStage("Preparing your document…");
      const docPdf = buildPdf(submittedAt);
      const blob = docPdf.output("blob");

      setSaveStage("Uploading…");
      const fileName = `referral_consent_${token}_${Date.now()}.pdf`;
      const storageRef = ref(storage, `referral_consents/${fileName}`);
      await uploadBytes(storageRef, blob, { contentType: "application/pdf" });
      const url = await getDownloadURL(storageRef);

      setSaveStage("Saving…");
      const submission = {
        token,
        recipientDocId: recipient?.id || "",
        // Site attribution: tag with the site from the URL (the folder this doc
        // is being written into), so it shows for the right site's staff the
        // instant it lands — no dependence on the recipient having a siteId. The
        // server (stampReferralSubmissionMrn) re-derives this from the doc's path
        // and is authoritative, so a tampered value is corrected. (B9)
        siteId: site,
        ...(recipient?.tenantId ? { tenantId: recipient.tenantId } : {}),
        name: name.trim(),
        dob,
        phone,
        email: email.trim(),
        address: address.trim(),
        weightManagementInterest: weightInterest || "not answered",
        conditions: confirmedConditions,
        printedName: printName.trim(),
        signerRelationship,
        signerRelationshipLabel: relationshipLabelFor(signerRelationship),
        signedByRepresentative: signerRelationship !== "self",
        consentPdfUrl: url,
        ipAddress: clientIP,
        deviceInfo: userAgent,
        submittedAt: serverTimestamp(),
        source: "ReferralQuestionnaire",
      };
      if (weightInterest === "yes") {
        submission.height = `${heightFt}'${heightIn}"`;
        submission.weightLbs = parseInt(weightLbs, 10) || 0;
        submission.bmi = parseFloat(bmi) || 0;
        submission.onGlp1WeightMgmt = weightGlp1 || "not answered";
      }
      await addDoc(collection(db, getReferralSubmissionsPathForSite(site)), submission);

      // Mark recipient as submitted (fail-soft)
      if (recipient?.id) {
        try {
          await updateDoc(doc(db, getReferralRecipientsPathForSite(site), recipient.id), {
            formSubmitted: true,
            formSubmittedAt: serverTimestamp(),
            token: recipient.token, // preserve token (rule requirement)
          });
        } catch (e) {
          console.warn("Could not mark recipient as submitted:", e);
        }
      }

      setDone(true);
    } catch (err) {
      console.error("Submit failed:", err);
      setSubmitError("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
      setSaveStage("");
    }
  };

  // ── Done state ───────────────────────────────────────────────────────────────
  if (done) {
    return (
      <Shell>
        <Card>
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center py-8"
          >
            <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/15 ring-1 ring-emerald-400/30 flex items-center justify-center mb-4">
              <FiCheckCircle className="text-emerald-300" size={32} />
            </div>
            <h2 className="text-xl font-bold text-white">All done — thank you!</h2>
            <p className="text-slate-300 mt-3 text-sm leading-relaxed">
              We received your information. Our team will review it and reach out if a program or study looks like a good fit.
            </p>
            <p className="text-slate-500 text-xs mt-4">You can close this page.</p>
          </motion.div>
        </Card>
      </Shell>
    );
  }

  // Hold the form back until we know whether this link is still live.
  if (!lookupSettled) {
    return (
      <Shell>
        <Card>
          <div className="flex flex-col items-center justify-center py-12">
            <div className="w-8 h-8 rounded-full border-2 border-slate-600 border-t-slate-200 animate-spin" />
            <p className="text-slate-400 text-sm mt-4">Opening your form…</p>
          </div>
        </Card>
      </Shell>
    );
  }

  // Single-use link: already submitted means spent. Offer a phone number rather
  // than a dead end — a patient who needs to correct an answer has to reach us.
  if (alreadySubmitted) {
    return (
      <Shell>
        <Card>
          <div className="text-center py-8">
            <div className="mx-auto w-16 h-16 rounded-full bg-emerald-500/15 ring-1 ring-emerald-400/30 flex items-center justify-center mb-4">
              <FiCheckCircle className="text-emerald-300" size={32} />
            </div>
            <h2 className="text-xl font-bold text-white">You've already completed this form</h2>
            <p className="text-slate-300 mt-3 text-sm leading-relaxed">
              This link has been used and is no longer active. We have your information
              and our team will reach out if a program or study looks like a good fit.
            </p>
            <p className="text-slate-400 text-xs mt-4 leading-relaxed">
              Need to change an answer? Call us at{" "}
              <a href="tel:+18149401212" className="text-slate-200 underline underline-offset-2">
                (814) 940-1212
              </a>
              .
            </p>
          </div>
        </Card>
      </Shell>
    );
  }

  // ── Progress bar ─────────────────────────────────────────────────────────────
  const ProgressBar = () => (
    <div className="flex items-center justify-center gap-2 mb-5">
      {STEPS.map((label, i) => {
        const n = i + 1;
        const active = step === n;
        const complete = step > n;
        return (
          <div key={label} className="flex items-center gap-2">
            <div className="flex flex-col items-center gap-0.5">
              <div
                className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold transition-colors ${
                  complete
                    ? "bg-indigo-500 text-white"
                    : active
                    ? "bg-gradient-to-r from-purple-500 to-indigo-500 text-white ring-1 ring-white/20"
                    : "bg-white/10 text-slate-400"
                }`}
              >
                {complete ? <FiCheck size={13} /> : n}
              </div>
              <span className={`text-[9px] font-medium ${active ? "text-purple-300" : "text-slate-500"}`}>
                {label}
              </span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`h-px w-5 sm:w-8 mb-3 ${step > n ? "bg-indigo-400/60" : "bg-white/15"}`} />
            )}
          </div>
        );
      })}
    </div>
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <Shell>
      <ProgressBar />
      <Card>
        <AnimatePresence mode="wait">

          {/* ── STEP 1: Personal info ─────────────────────────────────────── */}
          {step === 1 && (
            <motion.div key="s1" initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -14 }} transition={{ duration: 0.22 }}>
              <SectionHeader>About you</SectionHeader>

              <h2 className="text-xl font-bold text-white mb-1">Let's start with the basics</h2>
              <p className="text-slate-400 text-sm mb-5">Takes about 1 minute.</p>

              {/* Name */}
              <div className="mb-4">
                <FieldLabel>Full name <span className="text-rose-400">*</span></FieldLabel>
                <input
                  type="text"
                  name="name"
                  autoComplete="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="First and last name"
                  className={INPUT_CLS}
                />
              </div>

              {/* DOB — manually typed (autofillable via autoComplete="bday") with a
                  calendar-icon shortcut to the native date picker. Native
                  type="date" inputs don't support browser/device birthday
                  autofill, so the visible field is text and the date input is
                  just the picker trigger, kept in sync via handleDobPicked. */}
              <div className="mb-4">
                <FieldLabel>Date of birth <span className="text-rose-400">*</span></FieldLabel>
                <div className="relative">
                  <input
                    type="text"
                    name="bday"
                    inputMode="numeric"
                    autoComplete="bday"
                    placeholder="MM/DD/YYYY"
                    value={dobText}
                    onChange={(e) => handleDobTextChange(e.target.value)}
                    className={`${INPUT_CLS} pr-11`}
                  />
                  {/* Decorative icon only — the real tap target is the native
                      date input layered directly on top of it below. iOS only
                      opens its wheel picker on a genuine tap on the control
                      itself; triggering it via JS from a separate button (e.g.
                      showPicker()/focus()) is unreliable in Safari. */}
                  <div className="absolute right-1.5 top-1/2 -translate-y-1/2 w-8 h-8 flex items-center justify-center text-slate-400 pointer-events-none">
                    <FiCalendar size={16} />
                  </div>
                  <input
                    ref={dobPickerRef}
                    type="date"
                    aria-label="Choose date of birth from calendar"
                    value={dob}
                    onChange={(e) => handleDobPicked(e.target.value)}
                    max={new Date().toISOString().slice(0, 10)}
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 w-8 h-8 opacity-0 cursor-pointer [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:m-0"
                  />
                </div>
              </div>

              {/* Phone */}
              <div className="mb-4">
                <FieldLabel>Phone number</FieldLabel>
                <input
                  type="tel"
                  name="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className={INPUT_CLS}
                />
              </div>

              {/* Email */}
              <div className="mb-4">
                <FieldLabel>Email <span className="text-slate-500 font-normal normal-case">(optional)</span></FieldLabel>
                <input
                  type="email"
                  name="email"
                  inputMode="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className={INPUT_CLS}
                />
              </div>

              {/* Address — a <textarea> rather than a single-line <input>. Per the
                  autocomplete spec, "street-address" is meant to receive the
                  FULL multi-line address on a textarea; single-line inputs
                  only get line 1, which is why this was truncating before. */}
              <div className="mb-5">
                <FieldLabel>Home address <span className="text-slate-500 font-normal normal-case">(optional)</span></FieldLabel>
                <textarea
                  name="street-address"
                  autoComplete="street-address"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Street, City, State, ZIP"
                  rows={2}
                  className={`${INPUT_CLS} resize-none`}
                />
              </div>

              {s1Error && (
                <p className="text-rose-300 text-sm mb-3 flex items-center gap-1.5">
                  <FiAlertCircle size={14} /> {s1Error}
                </p>
              )}
              <PrimaryButton onClick={goStep2}>
                Continue <FiArrowRight size={18} />
              </PrimaryButton>
            </motion.div>
          )}

          {/* ── STEP 2: Weight management ─────────────────────────────────── */}
          {step === 2 && (
            <motion.div key="s2" initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -14 }} transition={{ duration: 0.22 }}>
              <SectionHeader>Your health</SectionHeader>
              <h2 className="text-xl font-bold text-white mb-5">
                Interested in our weight management and wellness program?
              </h2>

              <YesNo value={weightInterest} onChange={setWeightInterest} />

              <AnimatePresence initial={false}>
                {weightInterest === "yes" && (
                  <motion.div
                    key="weight-details"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-5 space-y-4">
                      {/* Height */}
                      <div>
                        <FieldLabel>Your height</FieldLabel>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Feet</p>
                            <select value={heightFt} onChange={(e) => setHeightFt(e.target.value)} className={SELECT_CLS}>
                              <option value="">ft</option>
                              {[4, 5, 6, 7].map((f) => <option key={f} value={f}>{f} ft</option>)}
                            </select>
                          </div>
                          <div>
                            <p className="text-[9px] text-slate-500 uppercase tracking-wider mb-1">Inches</p>
                            <select value={heightIn} onChange={(e) => setHeightIn(e.target.value)} className={SELECT_CLS}>
                              {Array.from({ length: 12 }, (_, i) => (
                                <option key={i} value={i}>{i} in</option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>

                      {/* Weight */}
                      <div>
                        <FieldLabel>Your weight (lbs)</FieldLabel>
                        <input
                          type="number"
                          inputMode="numeric"
                          min="1"
                          value={weightLbs}
                          onChange={(e) => setWeightLbs(e.target.value)}
                          placeholder="e.g. 165"
                          className={INPUT_CLS}
                        />
                      </div>

                      {/* BMI display */}
                      {bmi && bmiCategory && (
                        <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                          <div>
                            <p className="text-[10px] uppercase tracking-wider text-slate-400 mb-0.5">Your BMI</p>
                            <p className={`text-2xl font-bold ${bmiCategory.color}`}>{bmi}</p>
                            <p className={`text-xs font-medium mt-0.5 ${bmiCategory.color}`}>{bmiCategory.label}</p>
                          </div>
                          <div className="mt-3 pt-3 border-t border-white/10 text-xs text-slate-400 space-y-1 leading-relaxed">
                            <p>BMI is a number calculated from your height and weight.</p>
                            <p>
                              <span className="text-blue-300">Under 18.5</span> — Underweight &nbsp;
                              <span className="text-emerald-300">18.5–24.9</span> — Healthy &nbsp;
                              <span className="text-amber-300">25–29.9</span> — Overweight &nbsp;
                              <span className="text-rose-300">30+</span> — Obese
                            </p>
                          </div>
                        </div>
                      )}

                      {/* GLP-1 */}
                      <div>
                        <FieldLabel>Are you on any GLP-1 medication?</FieldLabel>
                        <p className="text-xs text-slate-500 mb-2">e.g. Ozempic, Wegovy, Mounjaro</p>
                        <YesNo value={weightGlp1} onChange={setWeightGlp1} />
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="mt-6 flex gap-3">
                <BackButton onClick={() => setStep(1)} />
                <div className="flex-1">
                  <PrimaryButton onClick={() => setStep(3)}>
                    Continue <FiArrowRight size={18} />
                  </PrimaryButton>
                </div>
              </div>
              <SkipLink onClick={() => setStep(4)} />
            </motion.div>
          )}

          {/* ── STEP 3: Conditions ────────────────────────────────────────── */}
          {step === 3 && (
            <motion.div key="s3" initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -14 }} transition={{ duration: 0.22 }}>
              <SectionHeader>Conditions</SectionHeader>
              <h2 className="text-xl font-bold text-white mb-4">
                {isFirstRound ? "Do you have any of these conditions?" : "Any other conditions?"}
              </h2>

              {/* Chips of already-added conditions */}
              {confirmedConditions.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {confirmedConditions.map((c) => (
                    <ConditionChip key={c.name} name={surveyConditionLabel(c.name)} onRemove={() => removeCondition(c.name)} />
                  ))}
                </div>
              )}

              {remainingConditions.length > 0 && (
                <div className="space-y-4">
                  {/* Condition dropdown */}
                  <select
                    value={conditionPick}
                    onChange={(e) => handleConditionPickChange(e.target.value)}
                    className={SELECT_CLS}
                  >
                    <option value="">Select a condition</option>
                    {remainingConditions.map((c) => (
                      <option key={c} value={c}>{surveyConditionLabel(c)}</option>
                    ))}
                    <option value="__none__">{noMoreOption}</option>
                  </select>

                  {/* Follow-up: exacerbation (Asthma, COPD, Bronchiectasis, Pulmonary Fibrosis) */}
                  <AnimatePresence initial={false}>
                    {followupType === "exacerbation" && (
                      <motion.div key="fu-exac" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                        <div className="rounded-xl border border-indigo-400/20 bg-indigo-500/[0.06] p-4">
                          <FieldLabel>In the last year, how many times were you prescribed antibiotics or steroids for {surveyConditionLabel(conditionPick)}?</FieldLabel>
                          <select value={conditionFollowup} onChange={(e) => { setConditionFollowup(e.target.value); setConditionError(""); }} className={SELECT_CLS}>
                            <option value="">Select</option>
                            {["0", "1", "2", "2+"].map((v) => <option key={v} value={v}>{v} {v === "0" ? "times" : v === "1" ? "time" : "times"}</option>)}
                          </select>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Follow-up: Diabetes GLP-1 */}
                  <AnimatePresence initial={false}>
                    {followupType === "glp1" && (
                      <motion.div key="fu-glp1" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                        <div className="rounded-xl border border-indigo-400/20 bg-indigo-500/[0.06] p-4">
                          <FieldLabel>Are you taking any GLP-1 medications for diabetes?</FieldLabel>
                          <YesNo value={conditionFollowup} onChange={(v) => { setConditionFollowup(v); setConditionError(""); }} />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Follow-up: Sleep Apnea CPAP */}
                  <AnimatePresence initial={false}>
                    {followupType === "cpap" && (
                      <motion.div key="fu-cpap" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                        <div className="rounded-xl border border-indigo-400/20 bg-indigo-500/[0.06] p-4">
                          <FieldLabel>Are you using a C-PAP at bedtime?</FieldLabel>
                          <YesNo value={conditionFollowup} onChange={(v) => { setConditionFollowup(v); setConditionError(""); }} />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {/* Error */}
                  {conditionError && (
                    <p className="text-rose-300 text-sm flex items-center gap-1.5">
                      <FiAlertCircle size={14} /> {conditionError}
                    </p>
                  )}

                  {/* Add another condition button (shows when a real condition + valid followup) */}
                  {conditionPick && conditionPick !== "__none__" && (!followupType || conditionFollowup) && (
                    <button
                      type="button"
                      onClick={() => confirmCondition()}
                      className="w-full py-2.5 rounded-xl border border-indigo-400/30 bg-indigo-500/[0.08] text-indigo-200 text-sm font-medium hover:bg-indigo-500/[0.15] transition"
                    >
                      + Add another condition
                    </button>
                  )}
                </div>
              )}

              {remainingConditions.length === 0 && (
                <p className="text-slate-400 text-sm">All conditions added.</p>
              )}

              <div className="mt-6 flex gap-3">
                <BackButton onClick={() => setStep(2)} />
                <div className="flex-1">
                  <PrimaryButton onClick={goStep4}>
                    Continue <FiArrowRight size={18} />
                  </PrimaryButton>
                </div>
              </div>
              <SkipLink onClick={() => setStep(4)} />
            </motion.div>
          )}

          {/* ── STEP 4: Consent & Sign ────────────────────────────────────── */}
          {step === 4 && (
            <motion.div key="s4" initial={{ opacity: 0, x: 14 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -14 }} transition={{ duration: 0.22 }}>
              <SectionHeader>Final step</SectionHeader>
              <h2 className="text-xl font-bold text-white mb-4">What you're agreeing to</h2>

              {/* Plain-language summary */}
              <div className="space-y-2.5 mb-5">
                {[
                  { title: "We'll look at your health information", sub: "So our team can see if you benefit from any one of our advanced clinical or research programs" },
                  { title: "This is not signing up for anything", sub: "It's only permission to look — nothing more." },
                  { title: "You can change your mind anytime", sub: "Write to us and we'll stop." },
                ].map(({ title, sub }) => (
                  <div key={title} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-3">
                    <span className="shrink-0 w-6 h-6 rounded-full bg-indigo-500/20 ring-1 ring-indigo-400/30 flex items-center justify-center mt-0.5">
                      <FiCheck className="text-indigo-300" size={13} />
                    </span>
                    <div>
                      <p className="font-semibold text-slate-100 text-sm leading-snug">{title}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{sub}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Collapsible full legal text */}
              <button
                type="button"
                onClick={() => setShowFullText((v) => !v)}
                className="text-purple-300 hover:text-purple-200 font-medium text-sm underline underline-offset-2 transition mb-2"
              >
                {showFullText ? "Hide full disclosure" : "Read full disclosure"}
              </button>
              <AnimatePresence initial={false}>
                {showFullText && (
                  <motion.pre
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden whitespace-pre-wrap font-sans text-xs text-slate-400 bg-black/20 border border-white/10 rounded-xl p-3 leading-relaxed mb-4"
                  >
                    {CONSENT_TEXT}
                  </motion.pre>
                )}
              </AnimatePresence>

              {/* Who is signing */}
              <div className="mt-4">
                <FieldLabel>Your relationship to the patient</FieldLabel>
                <select
                  value={signerRelationship}
                  onChange={(e) => setSignerRelationship(e.target.value)}
                  className={SELECT_CLS}
                >
                  {SIGNER_RELATIONSHIPS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {signerRelationship !== "self" && (
                  <p className="mt-1.5 text-xs text-slate-400">
                    By signing, you confirm you are authorized to act as the patient's
                    personal representative.
                  </p>
                )}
              </div>

              {/* Print name */}
              <div className="mt-4">
                <FieldLabel>
                  {signerRelationship === "self"
                    ? "Type your full name"
                    : "Type your full name (representative)"}
                </FieldLabel>
                <input
                  type="text"
                  name="signerName"
                  autoComplete="name"
                  placeholder="e.g. Jane Smith"
                  value={printName}
                  onChange={(e) => setPrintName(e.target.value)}
                  className={INPUT_CLS}
                />
              </div>

              {/* Agree checkbox */}
              <label className="flex items-start gap-3 mt-4 text-slate-200 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={approved}
                  onChange={(e) => {
                    setApproved(e.target.checked);
                    if (e.target.checked && !approvedAtRef.current) approvedAtRef.current = new Date();
                    setReviewError("");
                  }}
                  className="mt-0.5 w-5 h-5 accent-indigo-500 shrink-0"
                />
                <span>I agree to share my medical records with the research team.</span>
              </label>

              {reviewError && (
                <p className="text-rose-300 text-sm mt-3 flex items-center gap-1.5">
                  <FiAlertCircle size={14} /> {reviewError}
                </p>
              )}

              {/* Signature canvas */}
              <div className="mt-5">
                <FieldLabel>Sign your name below</FieldLabel>
                <div ref={containerRef} className="rounded-2xl p-1.5 bg-white/[0.06] border border-white/10">
                  <canvas
                    ref={canvasRef}
                    className="w-full h-[190px] touch-none rounded-xl bg-white"
                    onMouseDown={handleStart}
                    onMouseMove={handleMove}
                    onMouseUp={handleEnd}
                    onMouseLeave={handleEnd}
                    onTouchStart={handleStart}
                    onTouchMove={handleMove}
                    onTouchEnd={handleEnd}
                  />
                </div>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-xs text-slate-400">
                    {hasInk ? "Signature captured." : "Use your finger or stylus to sign above."}
                  </span>
                  <button type="button" onClick={handleClear} className="text-sm text-slate-300 hover:text-white underline underline-offset-2 transition">
                    Clear
                  </button>
                </div>
              </div>

              {submitError && (
                <p className="text-rose-300 text-sm mt-3 flex items-center gap-1.5">
                  <FiAlertCircle size={14} /> {submitError}
                </p>
              )}

              <div className="mt-6 flex gap-3">
                <BackButton onClick={() => !saving && setStep(3)} />
                <div className="flex-1">
                  <PrimaryButton onClick={handleSubmit} disabled={saving}>
                    {saving ? (
                      <><FiLoader className="animate-spin" size={18} /> {saveStage || "Submitting…"}</>
                    ) : (
                      <>Submit &amp; finish <FiCheck size={18} /></>
                    )}
                  </PrimaryButton>
                </div>
              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </Card>
    </Shell>
  );
}
